import rootSetters from '../actions';
import rootGetters from '../getters';
import { getNestedField, setNestedField } from '../utils';
import * as lodash from 'lodash';
import { dynamicField } from './utils';

export const blurRecoverForm =
  (field: FlattenKeys<BlurForm['recoverPassword']>, value = true) =>
  (dispatch: DispatchType) => {
    const {
      form: { blurForm },
    } = rootSetters(dispatch);
    blurForm({
      field: `recoverPassword.${field}`,
      value,
    });
  };

export const getRecoverFormValidation =
  (
    fields: Array<keyof BlurForm['recoverPassword']> = [
      'email',
      'password',
      'passwordConfirmation',
    ],
  ) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { blurForm },
    } = rootSetters(dispatch);
    const {
      form: { getBlurForm },
    } = rootGetters(getState());

    const fieldsObject = fields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});

    blurForm({
      field: 'recoverPassword',
      value: {
        ...getBlurForm('recoverPassword'),
        ...fieldsObject,
      },
    });

    const {
      form: { getRecoverFormMessage },
    } = rootGetters(getState());

    const fieldMessages = Object.fromEntries(
      Object.entries(getRecoverFormMessage()).filter(([key]) =>
        fields.includes(key as keyof BlurForm['recoverPassword']),
      ),
    );

    return !Object.values(fieldMessages).some((message) => {
      return message['0'];
    });
  };

export const resetRecoverForm =
  () => (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { setForm, blurForm },
    } = rootSetters(dispatch);

    const {
      form: { getForm },
    } = rootGetters(getState());

    setForm('recoverPassword', {
      ...getForm('recoverPassword'),
      email: '',
      password: '',
      passwordConfirmation: '',
    });
    blurForm({
      field: 'recoverPassword',
      value: {
        email: false,
        password: false,
        passwordConfirmation: false,
      },
    });
  };

export const setBatchForm =
  <T extends FlattenKeys<SingleBannerForm> | undefined | null | ''>(
    field: T,
    value: DeepType<
      T extends undefined | null | ''
        ? DeepPartial<SingleBannerForm>
        : SingleBannerForm,
      T
    >,
    forward?: ForwardField,
  ) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { setForm },
    } = rootSetters(dispatch);

    const {
      form: { getForm },
    } = rootGetters(getState());
    const pinnedSingleForms = getForm('batchBanner.batch.pinnedSingleForms');
    const singleForms = getForm('batchBanner.singleForms');
    const defaultSingleForm = getForm('batchBanner.defaultSingleForm');

    if (pinnedSingleForms.length > 0) {
      let newSingleForms: SingleBannerForm[] = lodash.mergeWith(
        [],
        [
          ...singleForms.filter(
            (singleForm) =>
              pinnedSingleForms.findIndex(
                (identifier) => identifier === singleForm.id,
              ) !== -1,
          ),
        ],
        (_, b) => (lodash.isArray(b) ? [...b] : undefined),
      );
      newSingleForms = newSingleForms.map((singleForm) => {
        singleForm = setNestedField(singleForm, field, value);
        forward?.targets.forEach(({ target, text }) => {
          singleForm = setNestedField(
            singleForm,
            target,
            dynamicField(singleForm, text),
          );
        });
        return singleForm;
      });

      setForm('batchBanner.singleForms', [
        ...singleForms.map((singleForm) =>
          pinnedSingleForms.findIndex(
            (identifier) => identifier === singleForm.id,
          ) !== -1
            ? newSingleForms.find(
                (newSingleForm) => singleForm.id === newSingleForm.id,
              )!
            : singleForm,
        ),
      ]);
    } else if (singleForms.length > 0) {
      let newSingleForms: SingleBannerForm[] = lodash.mergeWith(
        [],
        singleForms,
        (_, b) => (lodash.isArray(b) ? [...b] : undefined),
      );
      newSingleForms = newSingleForms.map((singleForm) => {
        singleForm = setNestedField(singleForm, field, value);
        forward?.targets.forEach(({ target, text }) => {
          singleForm = setNestedField(
            singleForm,
            target,
            dynamicField(singleForm, text),
          );
        });
        return singleForm;
      });
      setForm('batchBanner.singleForms', newSingleForms);
    } else {
      let newDefaultSingleForm: SingleBannerForm = lodash.merge(
        {},
        defaultSingleForm,
      );
      newDefaultSingleForm = setNestedField(newDefaultSingleForm, field, value);
      forward?.targets.forEach(({ target, text }) => {
        newDefaultSingleForm = setNestedField(
          newDefaultSingleForm,
          target,
          dynamicField(newDefaultSingleForm, text),
        );
      });
      setForm('batchBanner.defaultSingleForm', newDefaultSingleForm);
    }
  };

const formActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      blurRecoverForm: (
        field: FlattenKeys<BlurForm['recoverPassword']>,
        value = true,
      ) => dispatch<void>(blurRecoverForm(field, value)),
      getRecoverFormValidation: (
        fields?: Array<keyof BlurForm['recoverPassword']>,
      ) => dispatch<boolean>(getRecoverFormValidation(fields)),
      resetRecoverForm: () => dispatch<void>(resetRecoverForm()),
      setBatchForm: <
        T extends FlattenKeys<SingleBannerForm> | undefined | null | '',
      >(
        field: T,
        value: DeepType<
          T extends undefined | null | ''
            ? DeepPartial<SingleBannerForm>
            : SingleBannerForm,
          T
        >,
        forward?: ForwardField,
      ) => dispatch<void>(setBatchForm(field, value, forward)),
    },
  );

export default formActionCreators;
